/**
 * The catalogue of decorative course backgrounds.
 *
 * Each entry pairs an accent colour with two layers of line art:
 *
 *  - `tile` — a small motif drawn inside a 48×48 `<pattern>` that repeats to
 *    fill the whole band. Tiling (rather than one stretched picture) is what
 *    lets the same background sit in a 96×64 picker swatch and a 600×98 card
 *    header without distortion or cropping.
 *  - `hero` — one large glyph drawn on a 120×100 canvas, pinned to the right
 *    edge with `xMaxYMid meet` so it always scales to the band's height and
 *    never overlaps the text on the left.
 *
 * The artwork is line work drawn in `currentColor`, so the surface that hosts
 * it picks the ink: `CourseBackground` sets `color:#fff` and gets the familiar
 * white-on-accent band behind a subject card, while the timetable cell sets
 * `color: var(--sched-edge)` and gets a faint same-hue watermark under ink
 * text (W5.5). Either way it costs no network request or image storage — a
 * course only ever stores the key.
 *
 * Keys must stay in sync with the server's `CourseBackground` enum.
 */

const strokeGroup = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const GLYPH_FONT = {
  fontFamily: "var(--font-family-mono, 'JetBrains Mono', monospace)",
  fontWeight: 700,
  stroke: 'none',
  fill: 'currentColor',
};

/* ── Themes ─────────────────────────────────────────────────────────────── */

const THEMES = [
  {
    key: 'math',
    color: '#7c3aed',
    colorDeep: '#4c1d95',
    tile: (
      <g {...strokeGroup} strokeWidth="1.4">
        <path d="M8 14h10M13 9v10" />
        <circle cx="34" cy="33" r="4.5" />
        <circle cx="12" cy="38" r="1.4" fill="currentColor" stroke="none" />
        <path d="M28 12h8" />
      </g>
    ),
    hero: (
      <g {...strokeGroup} strokeWidth="3">
        <path d="M8 54h13l13 30 22-70h51" strokeWidth="3.5" />
        <path d="M60 82h26l-13-24z" />
        <circle cx="99" cy="70" r="13" />
      </g>
    ),
  },
  {
    key: 'physics',
    color: '#2563eb',
    colorDeep: '#1e3a8a',
    tile: (
      <g {...strokeGroup} strokeWidth="1.4">
        <ellipse cx="14" cy="15" rx="9" ry="4" />
        <circle cx="14" cy="15" r="1.6" fill="currentColor" stroke="none" />
        <path d="M28 34q5-5 9 0t9 0" />
      </g>
    ),
    hero: (
      <g {...strokeGroup} strokeWidth="2.6">
        <g transform="translate(60 38)">
          <ellipse rx="42" ry="15" />
          <ellipse rx="42" ry="15" transform="rotate(60)" />
          <ellipse rx="42" ry="15" transform="rotate(120)" />
          <circle r="7" fill="currentColor" stroke="none" />
        </g>
        {/* Baseline kept clear of y=100 so the descender is never clipped
            when the hero scales to the band's height. */}
        <text x="60" y="93" textAnchor="middle" fontSize="17" {...GLYPH_FONT}>
          E=mc²
        </text>
      </g>
    ),
  },
  {
    key: 'chemistry',
    color: '#0d9488',
    colorDeep: '#134e4a',
    tile: (
      <g {...strokeGroup} strokeWidth="1.4">
        <path d="M14 8l6 3.5v7L14 22l-6-3.5v-7z" />
        <circle cx="35" cy="33" r="2.6" fill="currentColor" stroke="none" />
        <path d="M28 40h9" />
      </g>
    ),
    hero: (
      <g {...strokeGroup} strokeWidth="2.6">
        <path d="M39 14h27M44 14v25L27 80q-3 8 5 8h37q8 0 5-8L57 39V14" />
        <path d="M33 66h38" strokeWidth="2" />
        <circle cx="45" cy="75" r="3.2" />
        <circle cx="56" cy="79" r="2.4" />
        <path d="M97 12l15 8.5v17L97 46l-15-8.5v-17z" />
        <circle cx="97" cy="29" r="7" strokeWidth="2" />
      </g>
    ),
  },
  {
    key: 'biology',
    color: '#16a34a',
    colorDeep: '#14532d',
    tile: (
      <g {...strokeGroup} strokeWidth="1.4">
        <path d="M8 8c10 5 0 11 10 16" />
        <path d="M18 8c-10 5 0 11-10 16" />
        <circle cx="35" cy="34" r="4" />
        <circle cx="35" cy="34" r="1.3" fill="currentColor" stroke="none" />
      </g>
    ),
    hero: (
      <g {...strokeGroup} strokeWidth="2.6">
        <path d="M28 10c48 16 48 64 0 80" />
        <path d="M72 10C24 26 24 74 72 90" />
        <g strokeWidth="2">
          <path d="M31 16h38M43 28h15M40 40h20M33 52h34M40 64h20M43 76h15M31 86h38" />
        </g>
        <path d="M88 68c0-20 15-32 28-34 1 16-11 32-28 34z" />
        <path d="M90 67l24-30" strokeWidth="1.8" />
      </g>
    ),
  },
  {
    key: 'informatics',
    color: '#155e75',
    colorDeep: '#083344',
    tile: (
      <g {...strokeGroup} strokeWidth="1.4">
        <path d="M12 8v14" />
        <circle cx="33" cy="33" r="4.5" />
        <path d="M28 12h6" />
        <circle cx="12" cy="38" r="1.4" fill="currentColor" stroke="none" />
      </g>
    ),
    hero: (
      <g {...strokeGroup} strokeWidth="3">
        <path d="M40 28L20 50l20 22" />
        <path d="M70 22L52 78" />
        <path d="M80 28l20 22-20 22" />
        <g strokeWidth="2">
          <path d="M6 90h20l8-9h22" />
          <circle cx="6" cy="90" r="3" />
          <circle cx="56" cy="81" r="3" />
        </g>
      </g>
    ),
  },
  {
    key: 'language',
    color: '#e11d48',
    colorDeep: '#881337',
    tile: (
      <g {...strokeGroup} strokeWidth="1.4">
        <rect x="7" y="9" width="17" height="12" rx="3.5" />
        <path d="M12 21v4l4-4" />
        <circle cx="35" cy="34" r="1.5" fill="currentColor" stroke="none" />
        <path d="M30 40h9" />
      </g>
    ),
    hero: (
      <g {...strokeGroup} strokeWidth="2.6">
        <rect x="10" y="14" width="70" height="46" rx="11" />
        <path d="M28 60v16l16-16" />
        <text x="45" y="45" textAnchor="middle" fontSize="24" {...GLYPH_FONT} opacity="0.9">
          Aa
        </text>
        <rect x="74" y="56" width="42" height="30" rx="8" strokeWidth="2.2" />
        <text x="95" y="77" textAnchor="middle" fontSize="15" {...GLYPH_FONT} opacity="0.9">
          Аа
        </text>
      </g>
    ),
  },
  {
    key: 'literature',
    // Terracotta, deliberately red-leaning: `history` sits on the yellow side
    // of the same warm family so the two are told apart at swatch size.
    color: '#9a3412',
    colorDeep: '#7c2d12',
    tile: (
      <g {...strokeGroup} strokeWidth="1.4">
        <path d="M7 11h14M7 17h14M7 23h9" />
        <circle cx="35" cy="34" r="1.5" fill="currentColor" stroke="none" />
        <path d="M30 40h9" />
      </g>
    ),
    hero: (
      <g {...strokeGroup} strokeWidth="2.6">
        <text x="60" y="26" textAnchor="middle" fontSize="30" {...GLYPH_FONT} opacity="0.75">
          “ ”
        </text>
        <path d="M60 42c-10-6-26-8-44 0v46c18-8 34-6 44 0z" />
        <path d="M60 42c10-6 26-8 44 0v46c-18-8-34-6-44 0z" />
        <path d="M60 42v46" strokeWidth="2" />
        <g strokeWidth="1.8" opacity="0.8">
          <path d="M24 56h24M24 66h24M72 56h24M72 66h24" />
        </g>
      </g>
    ),
  },
  {
    key: 'history',
    color: '#a16207',
    colorDeep: '#713f12',
    tile: (
      <g {...strokeGroup} strokeWidth="1.4">
        <path d="M9 9v14M15 9v14M21 9v14M6 25h18" />
        <circle cx="35" cy="34" r="4" />
        <path d="M35 31v3l2 1" />
      </g>
    ),
    hero: (
      <g {...strokeGroup} strokeWidth="2.6">
        <rect x="26" y="18" width="46" height="9" rx="2.5" />
        <path d="M35 27v52M45 27v52M55 27v52M64 27v52" strokeWidth="2" />
        <rect x="23" y="79" width="52" height="9" rx="2.5" />
        <path d="M86 20h30M86 84h30M89 20l13 22-13 22M113 20l-13 22 13 22" strokeWidth="2.2" />
      </g>
    ),
  },
  {
    key: 'geography',
    color: '#0369a1',
    colorDeep: '#0c4a6e',
    tile: (
      <g {...strokeGroup} strokeWidth="1.4">
        <path d="M5 15q7-7 14 0t14 0" />
        <path d="M12 24q7-7 14 0" />
        <circle cx="38" cy="36" r="1.5" fill="currentColor" stroke="none" />
      </g>
    ),
    hero: (
      <g {...strokeGroup} strokeWidth="2.4">
        <circle cx="46" cy="42" r="30" />
        <ellipse cx="46" cy="42" rx="13" ry="30" />
        <path d="M18 32h56M18 52h56" strokeWidth="2" />
        <path d="M72 90l18-32 13 20 9-14 10 26z" strokeWidth="2.4" />
      </g>
    ),
  },
  {
    key: 'art',
    color: '#c026d3',
    colorDeep: '#701a75',
    tile: (
      <g {...strokeGroup} strokeWidth="1.4">
        <circle cx="13" cy="15" r="4.5" fill="currentColor" stroke="none" opacity="0.85" />
        <circle cx="34" cy="34" r="4.5" />
        <path d="M28 10l5 6-5 6z" />
      </g>
    ),
    hero: (
      <g {...strokeGroup} strokeWidth="2.6">
        <path d="M12 52c0-22 18-38 38-36s30 18 28 34c-2 12-14 12-20 10-8-2-14 2-12 10 2 10-6 16-16 14-16-4-18-20-18-32z" />
        <g stroke="none" fill="currentColor" opacity="0.85">
          <circle cx="30" cy="42" r="4.5" />
          <circle cx="45" cy="32" r="4.5" />
          <circle cx="61" cy="35" r="4.5" />
          <circle cx="46" cy="62" r="4.5" />
        </g>
        <path d="M114 16L88 50" strokeWidth="3" />
        <path d="M86 48l8 8" strokeWidth="3" />
        <path d="M84 54l-6 12 12-6z" />
      </g>
    ),
  },
  {
    key: 'music',
    color: '#4f46e5',
    colorDeep: '#312e81',
    tile: (
      <g {...strokeGroup} strokeWidth="1.4">
        <ellipse cx="11" cy="20" rx="4.5" ry="3.4" fill="currentColor" stroke="none" opacity="0.85" />
        <path d="M15 19V6" />
        <path d="M28 36h12" />
        <circle cx="38" cy="12" r="1.4" fill="currentColor" stroke="none" />
      </g>
    ),
    hero: (
      <g {...strokeGroup} strokeWidth="2.4">
        <g strokeWidth="1.6" opacity="0.7">
          <path d="M6 24h108M6 36h108M6 48h108M6 60h108M6 72h108" />
        </g>
        <ellipse cx="44" cy="76" rx="10" ry="7.5" transform="rotate(-20 44 76)" fill="currentColor" stroke="none" opacity="0.9" />
        <ellipse cx="88" cy="66" rx="10" ry="7.5" transform="rotate(-20 88 66)" fill="currentColor" stroke="none" opacity="0.9" />
        <path d="M52 74V26M96 64V16" strokeWidth="3" />
        <path d="M52 26l44-10" strokeWidth="6" />
      </g>
    ),
  },
  {
    key: 'sport',
    color: '#ea580c',
    colorDeep: '#7c2d12',
    tile: (
      <g {...strokeGroup} strokeWidth="1.4">
        <circle cx="14" cy="15" r="6.5" />
        <path d="M14 8.5v-3M20.5 15h3M14 21.5v3M7.5 15h-3" />
        <circle cx="36" cy="36" r="1.5" fill="currentColor" stroke="none" />
      </g>
    ),
    hero: (
      <g {...strokeGroup} strokeWidth="2.6">
        <circle cx="48" cy="50" r="31" />
        <path d="M48 35l14 10-5 16H41l-5-16z" strokeWidth="2.2" />
        <path d="M48 35V20M62 45l16-6M57 61l9 15M39 61l-9 15M34 45l-16-6" strokeWidth="2" />
        <path d="M96 16v72" strokeWidth="3" />
        <path d="M96 22l22 7-22 11z" />
      </g>
    ),
  },
  {
    key: 'general',
    color: '#475569',
    colorDeep: '#1e293b',
    tile: (
      <g {...strokeGroup} strokeWidth="1.4">
        <path d="M6 14l8-4 8 4-8 4z" />
        <path d="M30 32h10M30 38h10" />
        <circle cx="36" cy="12" r="1.4" fill="currentColor" stroke="none" />
      </g>
    ),
    hero: (
      <g {...strokeGroup} strokeWidth="2.6">
        <path d="M60 18l48 18-48 18-48-18z" />
        <path d="M32 48v20c0 10 56 10 56 0V48" />
        <path d="M108 36v26" strokeWidth="2.2" />
        <circle cx="108" cy="66" r="4" fill="currentColor" stroke="none" />
        <path d="M14 76h24M14 84h24" strokeWidth="2.2" opacity="0.8" />
      </g>
    ),
  },
];

/* ── Lookup helpers ─────────────────────────────────────────────────────── */

/** Rendered when a course has no background, or an unknown one.
    Warm stone rather than an accent: "no subject picked yet" should read as
    unset, not as a thirteenth subject. */
export const NEUTRAL_BACKGROUND = {
  key: null,
  color: '#5c5346',
  colorDeep: '#2a251e',
  tile: (
    <g fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
      <path d="M10 10h6M32 32h6" />
    </g>
  ),
  hero: null,
};

const BY_KEY = THEMES.reduce((acc, theme) => {
  acc[theme.key] = theme;
  return acc;
}, {});

/** Every selectable theme, in catalogue order. */
export const COURSE_BACKGROUNDS = THEMES;

/** The theme for `key`, falling back to the neutral default. */
export const getCourseBackground = (key) =>
  (key && BY_KEY[String(key).toLowerCase()]) || NEUTRAL_BACKGROUND;

/** The accent colour for `key` — handy where only a colour is needed. */
export const getCourseBackgroundColor = (key) => getCourseBackground(key).color;

/* ── Inferring a theme from the subject's name ───────────────────────────── */

/**
 * The same subject-name rules the server seeds `background_key` with
 * (migration V36), kept in catalogue order because the first match wins and
 * some patterns are deliberately narrower than the ones below them.
 *
 * They exist here as well because a centre that adds a course without opening
 * the background picker leaves the column NULL, and a wall of neutral stone is
 * exactly what V36 set out to avoid. An admin's explicit choice always wins;
 * this only fills the gap where nothing was chosen.
 */
const NAME_RULES = [
  ['math', ['matemat', 'algebra', 'geometr', 'матема', 'math']],
  ['physics', ['fizik', 'физик', 'physic']],
  ['chemistry', ['kimyo', 'хими', 'chemi']],
  ['biology', ['biolog', 'биолог']],
  ['informatics', ['informat', 'информат', 'dasturl', 'programm', 'comput']],
  ['language', ['ingliz', 'rus tili', 'ona tili', 'англ', 'русск', 'english', 'язык', 'til']],
  ['literature', ['adabiy', 'литерат', 'literat']],
  ['history', ['tarix', 'истори', 'histor']],
  ['geography', ['geograf', 'геогра']],
  ['art', ['rasm', 'tasviriy', 'рисован', 'design', 'dizayn']],
  ['music', ['musiqa', 'музык', 'music']],
  ['sport', ['sport', 'jismoniy', 'спорт']],
];

/** The catalogue key a subject name implies, or null when nothing matches. */
export const inferCourseBackgroundKey = (name) => {
  if (!name) return null;
  const haystack = String(name).toLowerCase();
  // 'art' is matched exactly, not as a substring: it is short enough to hit
  // unrelated names (Startup, Kartografiya) that the later rules would then
  // never get to see. Same carve-out the migration makes.
  if (haystack.trim() === 'art') return 'art';
  const hit = NAME_RULES.find(([, needles]) => needles.some((n) => haystack.includes(n)));
  return hit ? hit[0] : null;
};

/**
 * The theme key to draw for a course: the one an admin chose, else the one its
 * name implies, else null for the neutral band.
 */
export const resolveCourseBackgroundKey = (key, name) =>
  (key && BY_KEY[String(key).toLowerCase()] ? String(key).toLowerCase() : null) ??
  inferCourseBackgroundKey(name);
